import * as React from 'react';
import GameBlock from '../../GameBlock/GameBlock.tsx';
import GameModal from '../../GameModal/GameModal.tsx';
import NextGameCountdown from '../../NextGameCountdown/NextGameCountdown.js';
import {getDisplayStatsForGameNumber} from '../../../utils/userStatsUtils.ts';
import {
  logShareSuccess,
  logShareFailure,
  logShareUnsupported
} from '../../../utils/loggingUtils.ts';
import useGameNavigation from '../../../utils/useGameNavigation.ts';
import { GameType } from '../../../types/GameType.ts';
import { getGameConfig, WeaverGameConfig } from '../../../types/GameConfig.ts';
import { getBlockColors } from '../../../utils/visualUtils.ts';
import { WeaverStatsModalProps } from './types';
import { getPlayOtherGameCards, SignInPrompt } from './sharedComponents.tsx';

import './GameStatsModal.css';

const WeaverStatsModal: React.FC<WeaverStatsModalProps> = (props) => {
  const gameNavigation = useGameNavigation(props.gameType);

  // Get strongly-typed display stats for Weaver games
  const displayStats = getDisplayStatsForGameNumber(props.gameType, props.gameNumber);

  function copyToClipboard(text: string): void {
    var dummy = document.createElement("textarea");
    document.body.appendChild(dummy);
    dummy.value = text;
    dummy.select();
    document.execCommand("copy");
    document.body.removeChild(dummy);
    props.onCopyToClipboard();
  }

  function shareOrCopyToClipboard(gameNumber: number, text: string): void {
    if (navigator.canShare && navigator.canShare({text: text})) {
      navigator
        .share({
          text: text,
        })
        .then(() => {
          logShareSuccess(props.gameType, gameNumber);
        })
        .catch(error => {
          logShareFailure(props.gameType, gameNumber, error);
          copyToClipboard(text);
        });
    } else {
      logShareUnsupported(props.gameType, gameNumber);
      copyToClipboard(text);
    }
  }

  function getEnteredWordsText(gameType: GameType, enteredWords: string[], endWord: string): string {
    let text = '';
    for (let i = 0; i < enteredWords.length; i++) {
      const enteredWord = enteredWords[i];
      const colors = getBlockColors(enteredWord, endWord, gameType);
      const greenCount = colors.filter(c => c === 'green').length;
      const yellowCount = colors.filter(c => c === 'yellow').length;
      const whiteCount = colors.length - greenCount - yellowCount;
      text += '🟩'.repeat(greenCount) + '🟨'.repeat(yellowCount) + '⬜'.repeat(whiteCount) + '\n';
    }
    return text;
  }

  function shareResultsOrCopyToClipboard(
    gameType: GameType,
    gameNumber: number, 
    startWord: string, 
    endWord: string, 
    enteredWords: string[]
  ): void {
    const gameConfig = getGameConfig(gameType);
    const gameName = gameConfig.name;
    let text = gameName;
    
    // Get special emoji for Weaver games
    let specialEmoji: string | null = null;
    if (gameType === GameType.WEAVER || gameType === GameType.WEAVER_X) {
      const weaverConfig = gameConfig as WeaverGameConfig;
      specialEmoji = weaverConfig.getSpecialEmoji(gameNumber);
    }
    text += specialEmoji != null ? specialEmoji : ' ';
    text += '#' + gameNumber + '\n\n';
    text += startWord.toUpperCase() + '\n';
    text += getEnteredWordsText(gameType, enteredWords, endWord);
    text += endWord.toUpperCase();
    shareOrCopyToClipboard(gameNumber, text);
  }

  function getArchiveActionButtons(): React.ReactElement {
    const gameConfig = getGameConfig(props.gameType);
    const nextGameNumber = props.gameNumber + 1;
    const hasNextGame = gameConfig.isValidGameNumber(nextGameNumber);
    const nextLabel = hasNextGame
      ? `Play next ${gameConfig.name} (#${nextGameNumber})`
      : `Play today's ${gameConfig.name}`;

    return (
      <>
        <div
          className='playOtherGameCard'
          onClick={props.onPlayNext}
          style={{ backgroundColor: gameConfig.primaryColor }}
        >
          <div className='playOtherGameButtonText'>
            {nextLabel}
          </div>
        </div>
        <div className='playOtherGameCard playOtherGameCardToday' onClick={props.onReturnToArchive}>
          <div className='playOtherGameButtonText'>
            Return to Archives
          </div>
        </div>
      </>
    );
  }


  function getTodayModalContent() {
    const gameConfig = getGameConfig(props.gameType);
    
    return (
      <div id='gameStatsModalContent'>
        <div className='statsModalRow'>
          {displayStats.minPathLength != null &&
            <div className='userStat'>
              <GameBlock character={displayStats.optimalPathLength.toString()} classNames={['userStatsBlock']} />
              <div className='userStatLabel'>
                Today's Optimal
              </div>
            </div>
          }
          <div className='userStat'>
            {displayStats.minPathLength == null ?
              <GameBlock character='?' classNames={['userStatsBlock']} /> :
              <GameBlock character={displayStats.minPathLength.toString()} classNames={['successUserStatsBlock']} />
            }
            <div className='userStatLabel'>
              Today's Score
            </div>
          </div>
          {displayStats.minPathLength != null &&
            <div className='shareTodaysScoreButtonContainer'>
              <button className='shareTodaysScoreButton' 
                onClick={() => 
                  shareResultsOrCopyToClipboard(
                    props.gameType,
                    props.gameNumber,
                    displayStats.startWord,
                    displayStats.endWord,
                    displayStats.minPathWords ?? [],
                  )}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
                </svg>
                <div className='shareButtonText'>
                  Share
                </div>
              </button>
            </div>
          }
        </div>
        <div className='statsModalRow'>
          <div className='userStat'>
            <GameBlock character={displayStats.numSolved.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              Solved
            </div>
          </div>
          <div className='userStat'>
            <GameBlock character={displayStats.longestStreak.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              Longest Streak
            </div>
          </div>
          <div className='userStat'>
            <GameBlock character={displayStats.currentStreak.toString()} classNames={['userStatsBlock']} />
            <div className='userStatLabel'>
              Current Streak
            </div>
          </div>
        </div>
        <div className='statsModalRow'>
          <div className='nextGameCountdownContainer'>
            <NextGameCountdown />
            <div>Next {gameConfig.name}</div>
          </div>
        </div>
        <>
          {getPlayOtherGameCards({
            currentGameType: props.gameType,
            onGameSelect: (gameType) => gameNavigation.redirectToGame(gameType, 'game_stats_modal')
          })}
        </>
        <SignInPrompt onClose={props.onClose} />
      </div>
    );
  }

  function getArchivedModalContent() {
    return (
      <div id='gameStatsModalContent'>
        <div className='statsModalRow'>
          {displayStats.minPathLength != null &&
            <div className='userStat'>
              <GameBlock character={displayStats.optimalPathLength.toString()} classNames={['userStatsBlock']} />
              <div className='userStatLabel'>
                Optimal
              </div>
            </div>
          }
          <div className='userStat'>
            {displayStats.minPathLength == null ?
              <GameBlock character='?' classNames={['userStatsBlock']} /> :
              <GameBlock character={displayStats.minPathLength.toString()} classNames={['successUserStatsBlock']} />
            }
            <div className='userStatLabel'>
              Score
            </div>
          </div>
          {displayStats.minPathLength != null &&
            <div className='shareTodaysScoreButtonContainer'>
              <button className='shareTodaysScoreButton' 
                onClick={() => 
                  shareResultsOrCopyToClipboard(
                    props.gameType,
                    props.gameNumber,
                    displayStats.startWord,
                    displayStats.endWord,
                    displayStats.minPathWords ?? [],
                  )}>
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                  <path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92c0-1.61-1.31-2.92-2.92-2.92zM18 4c.55 0 1 .45 1 1s-.45 1-1 1-1-.45-1-1 .45-1 1-1zM6 13c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm12 7.02c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
                </svg>
                <div className='shareButtonText'>
                  Share
                </div>
              </button>
            </div>
          }
        </div>
        {getArchiveActionButtons()}
      </div>
    );
  }

  function getModalContent() {
    const gameConfig = getGameConfig(props.gameType);
    if (gameConfig.isGameNumberCurrentGame(props.gameNumber)) {
      return getTodayModalContent();
    } else {
      return getArchivedModalContent();
    }
  }

  return (
    <GameModal
      title='Statistics'
      content={getModalContent()}
      isOpen={props.isOpen}
      onClose={props.onClose}
      modalStyle={{ paddingBottom: 0 }}
    />
  );
};

export default WeaverStatsModal; 